import { Alert, AlertDescription, AlertMedia, AlertTitle } from 'components/ui/alert';
import { Button } from 'components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetTitle } from 'components/ui/sheet';
import { BadgeCheckIcon, LockIcon, ShieldEllipsisIcon, Trash2Icon } from 'lucide-react';
import posthog from 'posthog-js';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectHasAccessWithLicenseCode } from 'store/users/selectors';

/**
 * « Votre vie privée, respectée », opening what the caregivers see of the
 * patient's data and what protects it (Figma 3b-Messaging-Privacy).
 */
export default function PrivacyAlert() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // Only a licence code comes from an employer
  const hasAccessWithLicenseCode = useSelector(selectHasAccessWithLicenseCode);

  const guarantees = [
    { icon: <LockIcon />, label: t('messagingV3.privacy.encrypted') },
    { icon: <ShieldEllipsisIcon />, label: t('messagingV3.privacy.hosting') },
    { icon: <BadgeCheckIcon />, label: t('messagingV3.privacy.gdpr') },
    { icon: <Trash2Icon />, label: t('messagingV3.privacy.deletion') },
  ];

  return (
    <>
      <Alert
        asChild
        variant="elevated"
        onClick={() => {
          posthog.capture('messaging.privacy_opened');
          setOpen(true);
        }}
      >
        <button type="button" className="cursor-pointer text-left">
          <AlertMedia>
            <LockIcon aria-hidden />
          </AlertMedia>
          <AlertTitle>{t('messagingV3.privacy.title')}</AlertTitle>
          <AlertDescription>{t('messagingV3.privacy.description')}</AlertDescription>
        </button>
      </Alert>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="max-h-[90dvh] overflow-y-auto">
          <div className="flex flex-col gap-6 px-4 pt-12 pb-6">
            <div className="flex flex-col gap-6 px-2">
              <SheetTitle asChild>
                <h2>{t('messagingV3.privacy.sheetTitle')}</h2>
              </SheetTitle>
              <SheetDescription className="text-primary-foreground text-base">
                {hasAccessWithLicenseCode ? t('messagingV3.privacy.sheetTextB2B') : t('messagingV3.privacy.sheetText')}
              </SheetDescription>
            </div>
            <Alert variant="success" className="gap-y-3">
              <AlertTitle className="text-xs font-bold tracking-widest uppercase">
                {t('messagingV3.privacy.caregiversSeeTitle')}
              </AlertTitle>
              <AlertDescription className="text-base text-foreground">
                <Trans i18nKey="messagingV3.privacy.caregiversSee" components={{ b: <b /> }} />
              </AlertDescription>
            </Alert>
            <ul className="flex flex-col px-4">
              {guarantees.map(({ icon, label }) => (
                <li
                  key={label}
                  className="flex items-center gap-3 border-b py-3.5 text-sm text-muted-foreground last:border-b-0 [&>svg]:size-4.5 [&>svg]:shrink-0"
                >
                  {icon}
                  {label}
                </li>
              ))}
            </ul>
            <SheetClose asChild>
              <Button size="xl" className="w-full">
                {t('messagingV3.privacy.ok')}
              </Button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
